import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ArrowRight, Coffee, BookOpen, Briefcase, Heart, Zap, TrendingUp, Users, Timer, Youtube, User, Sparkles, CheckCircle2, Clock, Target, Video, Bell, BookCheck, UsersRound, CheckSquare } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const IndexTw = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="CSW - 自修室 | 免費線上讀書室 | Online Workspace | Productivity Tool | Study Stream"
        description="免費自修室工具，提升專注力與學習習慣。24小時開放、番茄鐘計時器、與全球學生一起學習。"
        keywords="自修室, 線上學習空間, 線上讀書, 學習直播, 番茄鐘"
        canonicalUrl="/tw/"
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">免費使用 • 無限制</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  您的個人{" "}
                  <span className="text-gradient">自修室</span>{" "}
                  工具
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  放鬆。學習。工作。
                </p>
                
                <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                  CSW 透過全方位的自修室，為全球學習者帶來全新的線上學習方式。無論您是在準備考試、處理大型專案，還是只需要一個高效的學習環境，CSW 都是您與來自世界各地志同道合的夥伴一起學習的理想平台。
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      立即加入自修室
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      試用測試版
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">100k+</p>
                    <p className="text-sm text-muted-foreground">完成時數</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">8k+</p>
                    <p className="text-sm text-muted-foreground">完成任務</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">免費</p>
                    <p className="text-sm text-muted-foreground">永久免費</p>
                  </div>
                </div>
              </div>
              
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl animate-slide-in" style={{ animationDelay: '0.4s' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">目前在線</p>
                      <p className="text-2xl font-bold text-foreground">554 位學習者</p>
                    </div>
                    <div className="flex -space-x-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary border-2 border-card" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Modes Section */}
        <section className="py-20 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background" />
          
          <div className="container mx-auto relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                選擇您的模式
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                無論您是在放鬆、學習還是工作，CSW 都能滿足您的需求
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  id: "chill",
                  title: "放鬆",
                  description: "在舒適的環境中放鬆並與他人聯繫",
                  icon: Coffee,
                  color: "hsl(328 85% 55%)",
                  features: [
                    { icon: Video, label: "直播" },
                    { icon: Users, label: "朋友" },
                  ],
                },
                {
                  id: "study",
                  title: "學習",
                  description: "使用結構化工具和責任機制專注學習",
                  icon: BookOpen,
                  color: "hsl(193 95% 50%)",
                  features: [
                    { icon: Timer, label: "番茄鐘" },
                    { icon: BookCheck, label: "測驗" },
                    { icon: UsersRound, label: "小組" },
                  ],
                },
                {
                  id: "work",
                  title: "工作",
                  description: "透過任務管理和提醒保持生產力",
                  icon: Briefcase,
                  color: "hsl(38 92% 50%)",
                  features: [
                    { icon: CheckSquare, label: "任務" },
                    { icon: Bell, label: "提醒" },
                  ],
                },
              ].map((mode, index) => {
                const Icon = mode.icon;
                return (
                  <Card
                    key={mode.id}
                    className="group relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 hover:scale-105 hover:shadow-xl animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                      style={{ background: `linear-gradient(135deg, ${mode.color} 0%, transparent 100%)` }}
                    />
                    
                    <CardHeader className="text-center pb-4">
                      <div 
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110"
                        style={{ backgroundColor: `${mode.color}15` }}
                      >
                        <Icon 
                          className="w-8 h-8 transition-colors duration-300" 
                          style={{ color: mode.color }}
                        />
                      </div>
                      <CardTitle className="text-2xl font-bold" style={{ color: mode.color }}>
                        {mode.title}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {mode.description}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-3">
                        {mode.features.map((feature) => {
                          const FeatureIcon = feature.icon;
                          return (
                            <div 
                              key={feature.label}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent/50 transition-colors duration-200"
                            >
                              <FeatureIcon className="w-5 h-5 text-muted-foreground" />
                              <span className="text-foreground font-medium">{feature.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-20 left-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          
          <div className="container mx-auto relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <div className="inline-block mb-4 px-6 py-2 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full border border-primary/30">
                <span className="text-sm font-semibold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  經過驗證的成果
                </span>
              </div>
              <h2 className="font-display text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                改變您的學習生活
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                由研究支持並經全球數千名用戶驗證，CSW 在專注力、連結和生產力方面提供可衡量的改善。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-20">
              {[
                {
                  icon: Heart,
                  title: "停止孤獨",
                  stat: "89%",
                  statLabel: "感覺更有連結",
                  description: "加入全球專注個人社群。與他人一起學習，分享您的進度，並透過共同的生產力目標建立有意義的連結。",
                  benefits: [
                    "與他人即時自修室",
                    "支持性的社群氛圍",
                    "跨時區連結",
                  ],
                  gradient: "from-primary to-primary/60",
                },
                {
                  icon: Zap,
                  title: "擊敗拖延",
                  stat: "3倍",
                  statLabel: "完成更多任務",
                  description: "結構化的工作流程和責任機制幫助您立即開始任務。他人工作的存在創造自然動力，讓您開始並保持專注。",
                  benefits: [
                    "專注工作的番茄鐘計時器",
                    "視覺化進度追蹤",
                    "同儕責任系統",
                  ],
                  gradient: "from-secondary to-secondary/60",
                },
                {
                  icon: TrendingUp,
                  title: "提升生產力",
                  stat: "2.5倍",
                  statLabel: "產出增加",
                  description: "使用經過驗證的生產力技術優化您的工作流程。追蹤您的進度，有效管理任務，並透過我們的整合工具在更短的時間內完成更多工作。",
                  benefits: [
                    "智慧任務管理",
                    "時間追蹤和分析",
                    "無干擾工作空間",
                  ],
                  gradient: "from-primary via-secondary to-primary",
                },
              ].map((impact, index) => {
                const Icon = impact.icon;
                return (
                  <Card
                    key={impact.title}
                    className="group relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl animate-fade-in bg-card/50 backdrop-blur-sm"
                    style={{ animationDelay: `${index * 0.15}s` }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${impact.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                    
                    <CardContent className="p-8">
                      <div className="mb-6">
                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${impact.gradient} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                          <Icon className="w-8 h-8 text-primary-foreground" />
                        </div>
                      </div>

                      <h3 className="text-2xl font-bold mb-4 text-foreground">
                        {impact.title}
                      </h3>

                      <div className="mb-6">
                        <div className={`text-5xl font-bold bg-gradient-to-r ${impact.gradient} bg-clip-text text-transparent mb-2`}>
                          {impact.stat}
                        </div>
                        <div className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                          {impact.statLabel}
                        </div>
                      </div>

                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {impact.description}
                      </p>

                      <div className="space-y-3">
                        {impact.benefits.map((benefit) => (
                          <div key={benefit} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-foreground">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center animate-fade-in" style={{ animationDelay: "0.6s" }}>
              <Card className="max-w-4xl mx-auto border-2 border-primary/20 bg-gradient-to-br from-card to-accent/20 backdrop-blur-sm">
                <CardContent className="p-12">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="flex-1 text-left">
                      <h3 className="font-display text-3xl font-bold mb-3 text-foreground">
                        加入數千名已改變生產力的用戶
                      </h3>
                      <p className="text-muted-foreground text-lg">
                        今天就開始您的旅程，邁向更好的專注力、更強的連結和更高的成就。
                      </p>
                    </div>
                    <div className="flex flex-col gap-4 items-center">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        <span className="text-sm font-semibold text-foreground">50,000+ 活躍用戶</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-secondary" />
                        <span className="text-sm font-semibold text-foreground">2M+ 小時記錄</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-primary" />
                        <span className="text-sm font-semibold text-foreground">95% 滿意度</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Alone but Never Lonely Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/5 to-background" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto items-center">
              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <h2 className="text-4xl font-bold text-foreground mb-4">獨處，但不孤單</h2>
                <p className="text-xl text-muted-foreground">
                  這是一個特別適合內向人士的環境，您可以安靜地學習，同時自然地與他人建立連結。
                </p>
              </Card>

              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <Heart className="w-12 h-12 text-primary" />
                  <h2 className="text-4xl font-bold text-foreground">寧靜時光</h2>
                </div>
                <p className="text-xl text-muted-foreground">
                  在需要專注的時刻，為您提供安靜的環境。
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted via-background to-background" />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4 animate-fade-in">
              <h2 className="font-display text-4xl sm:text-5xl font-bold">
                如何提高 <span className="text-gradient">生產力</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                選擇您的完美模式，釋放您的全部潛力
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: Users,
                  title: "小組模式",
                  description: "與來自世界各地的同儕一起學習。加入虛擬房間並保持動力。",
                  gradient: "from-primary/20 to-primary/5",
                  iconColor: "text-primary",
                },
                {
                  icon: Timer,
                  title: "計時器模式",
                  description: "使用內建番茄鐘計時器提升專注力。聰明工作，而非努力工作。",
                  gradient: "from-secondary/20 to-secondary/5",
                  iconColor: "text-secondary",
                },
                {
                  icon: Youtube,
                  title: "YT 模式",
                  description: "在工作時串流教育內容。持續學習和成長。",
                  gradient: "from-accent/20 to-accent/5",
                  iconColor: "text-accent",
                },
                {
                  icon: User,
                  title: "單人模式",
                  description: "在您的個人工作空間中專注。隱私與生產力並存。",
                  gradient: "from-muted-foreground/20 to-muted-foreground/5",
                  iconColor: "text-foreground",
                },
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className="group relative overflow-hidden border-border bg-card hover:bg-card/80 p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                    
                    <div className="relative z-10 space-y-4">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className={`h-7 w-7 ${feature.iconColor}`} />
                      </div>
                      
                      <h3 className="text-xl font-bold text-foreground">
                        {feature.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                    
                    <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-primary/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Study Stream Section */}
        <section className="py-24 px-4 relative overflow-hidden bg-gradient-to-b from-background to-muted">
          <div className="container mx-auto relative z-10">
            <div className="text-center mb-12 animate-fade-in">
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">
                24/7 <span className="text-gradient">自修室直播</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                隨時加入我們的即時學習社群
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="border-2 border-primary/20 bg-card/50 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      CSW 的自修室全天候開放，提供資源、社群支援和互動式小組學習，確保您隨時獲得陪伴。從尋找學習夥伴保持責任感、接受導師指導，到有意義的休息時間，CSW 都能滿足您的需求。
                    </p>
                    <div className="flex justify-center pt-4">
                      <Button 
                        size="lg"
                        className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8"
                        asChild
                      >
                        <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                          開始免費直播
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-muted to-background" />
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          </div>
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center space-y-12 animate-fade-in">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">加入社群</span>
                </div>
                
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  獨自。連結。{" "}
                  <span className="text-gradient">賦能。</span>
                </h2>
                
                <p className="text-xl sm:text-2xl text-muted-foreground max-w-2xl mx-auto font-light">
                  今天就開始您的生產力之旅。無需信用卡。
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  size="lg"
                  className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-xl glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    立即試用
                    <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-border hover:bg-card font-semibold px-10 py-7 text-xl transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    觀看運作方式
                  </a>
                </Button>
              </div>
              
              <div className="pt-8 space-y-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <p className="text-sm text-muted-foreground uppercase tracking-wider font-semibold">
                  受到全球學生信賴
                </p>
                <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
                  {['大學生', '專業人士', '創作者', '研究人員'].map((group) => (
                    <div key={group} className="text-muted-foreground font-medium">
                      {group}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default IndexTw;
